import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Globe2, Search, X } from "lucide-react";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import arLabels from "react-phone-number-input/locale/ar";
import enLabels from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import { getLang } from "../../i18n";
import { isInternationalPhoneValid, toE164Phone } from "../../utils/phone";

const COUNTRY_ORDER = ["LY", "EG", "SA", "AE", "TN", "DZ", "MA", "SD", "JO", "IQ", "QA", "KW", "BH", "OM", "GB", "US", "|", "..."];

const COPY = {
  ar: {
    search: "ابحث باسم الدولة أو المفتاح",
    empty: "لا توجد دولة مطابقة",
    hint: "اختر الدولة ثم اكتب الرقم المحلي",
    invalid: "تحقق من رقم الهاتف وكود الدولة",
    select: "اختر الدولة",
  },
  en: {
    search: "Search country or dial code",
    empty: "No matching country",
    hint: "Choose a country, then enter the local number",
    invalid: "Check the phone number and country code",
    select: "Choose country",
  },
};

function countryFlag(country) {
  if (!country) return "🌐";
  return country
    .toUpperCase()
    .split("")
    .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join("");
}

function SearchableCountrySelect({ value, onChange, options, disabled, readOnly, onFocus, onBlur, isArabic, ...rest }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const copy = isArabic ? COPY.ar : COPY.en;

  const countries = useMemo(() => options.filter((option) => option.value && !option.divider), [options]);
  const filtered = useMemo(() => {
    const search = query.trim().toLocaleLowerCase(isArabic ? "ar" : "en").replace(/^\+/, "");
    if (!search) return countries;
    return countries.filter((option) => {
      const dialCode = getCountryCallingCode(option.value);
      return option.label.toLocaleLowerCase(isArabic ? "ar" : "en").includes(search)
        || option.value.toLowerCase().includes(search)
        || dialCode.includes(search);
    });
  }, [countries, isArabic, query]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectCountry = (country) => {
    onChange(country);
    setOpen(false);
    setQuery("");
  };

  const selectedLabel = countries.find((country) => country.value === value)?.label || copy.select;
  const dialCode = value ? getCountryCallingCode(value) : "";
  const ariaLabel = rest["aria-label"] || copy.select;

  return (
    <div className="international-country-picker" ref={rootRef} dir={isArabic ? "rtl" : "ltr"}>
      <button
        type="button"
        className="international-country-trigger"
        aria-label={`${ariaLabel}: ${selectedLabel}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled || readOnly}
        onClick={() => setOpen((current) => !current)}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <span className="international-country-flag" aria-hidden="true">{countryFlag(value)}</span>
        {dialCode && <span className="international-country-code" dir="ltr">+{dialCode}</span>}
        <ChevronDown size={15} aria-hidden="true" />
      </button>

      {open && (
        <div className="international-country-popover" role="dialog" aria-label={ariaLabel}>
          <div className="international-country-search">
            <Search size={16} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.search}
              aria-label={copy.search}
            />
            {query && <button type="button" onClick={() => setQuery("")} aria-label={isArabic ? "مسح البحث" : "Clear search"}><X size={15} /></button>}
          </div>
          <div className="international-country-list" role="listbox">
            {filtered.length ? filtered.map((country) => (
              <button
                id={`country-${country.value}`}
                key={country.value}
                type="button"
                role="option"
                aria-selected={country.value === value}
                className={country.value === value ? "is-selected" : ""}
                onClick={() => selectCountry(country.value)}
              >
                <span className="international-country-flag" aria-hidden="true">{countryFlag(country.value)}</span>
                <span className="international-country-name">{country.label}</span>
                <bdi className="international-country-code" dir="ltr">+{getCountryCallingCode(country.value)}</bdi>
                {country.value === value && <Check className="international-country-check" size={16} aria-hidden="true" />}
              </button>
            )) : <p className="international-country-empty">{copy.empty}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function InternationalPhoneField({
  id,
  label,
  value,
  onChange,
  defaultCountry = "LY",
  language,
  required = false,
  disabled = false,
  autoFocus = false,
  placeholder,
  helperText,
  showValidation = true,
  compact = false,
  className = "",
  onBlur,
  ...inputProps
}) {
  const isArabic = (language || getLang()) === "ar";
  const copy = isArabic ? COPY.ar : COPY.en;
  const normalizedValue = toE164Phone(value, defaultCountry) || undefined;
  const invalid = showValidation && Boolean(value) && !isInternationalPhoneValid(value, defaultCountry);

  return (
    <div className={`international-phone-field ${compact ? "international-phone-compact" : ""} ${invalid ? "has-error" : ""} ${className}`.trim()} dir={isArabic ? "rtl" : "ltr"}>
      {label && <label className="field-label" htmlFor={id}>{label}</label>}
      <PhoneInput
        id={id}
        name={inputProps.name}
        className="international-phone-control"
        defaultCountry={defaultCountry}
        countryOptionsOrder={COUNTRY_ORDER}
        addInternationalOption={false}
        labels={isArabic ? arLabels : enLabels}
        locales={isArabic ? "ar" : "en"}
        countrySelectComponent={SearchableCountrySelect}
        countrySelectProps={{ isArabic }}
        value={normalizedValue}
        onChange={(phone) => onChange(phone || "")}
        limitMaxLength
        international={false}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete="tel"
        placeholder={placeholder || (isArabic ? "رقم الهاتف" : "Phone number")}
        aria-invalid={invalid}
        onBlur={onBlur}
        {...inputProps}
      />
      {!compact && (
        <p className={`international-phone-helper ${invalid ? "is-error" : ""}`}>
          <Globe2 size={14} aria-hidden="true" />
          {invalid ? copy.invalid : (helperText || copy.hint)}
        </p>
      )}
    </div>
  );
}
